import { useSafeLeakSubscriber, useSubject } from '@ingenico/react-utils/hooks';
import { useUnmountEffect } from '@react-hookz/web';
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { Subject } from 'rxjs';
import { delay, retry, tap } from 'rxjs';
import { TemConnectionBodySteps } from '../tem-connection-modal-body/temConnectionModalBody.model';
import type { ScreenShareData, UseModalScreenShareParams, UseModalScreenShareReturn } from './modalScreenShare.model';
import { getModalCurrentStep } from './modalScreenShare.util';

export function useModalScreenShare({
  ref,
  onClose,
  onConnectionSucceed,
  isConnecting,
  isFailure,
  isNotAllowed,
  isSuccess,
  triggerSignatureDevicePermissionCall,
  isRefusedByUser,
}: UseModalScreenShareParams): UseModalScreenShareReturn {
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);

  const subscriber = useSafeLeakSubscriber();
  const trigger: Subject<void> = useSubject();
  const destroy: Subject<boolean> = useSubject();
  const subscribed = useRef<boolean>(false);
  const modalOpened = useRef<boolean>(false);

  useUnmountEffect(() => {
    subscribed.current = false;
    modalOpened.current = false;
  });

  useEffect(() => {
    if (!subscribed.current) {
      subscribed.current = true;
      subscriber(
        trigger.asObservable().pipe(
          tap(() => {
            setIsOpenModal(true);
            triggerSignatureDevicePermissionCall?.();
          }),
          delay(2000),
          retry(),
        ),
      );
    }
  }, [subscriber, trigger, destroy, onConnectionSucceed, triggerSignatureDevicePermissionCall]);

  useEffect(() => {
    if (!modalOpened.current) {
      modalOpened.current = true;
      trigger.next(void 0);
    }
  }, [trigger]);

  const tryCheckDeviceConnection = useCallback(() => trigger.next(void 0), [trigger]);

  const screenShareData = useMemo<ScreenShareData>(
    () => ({
      open: isOpenModal,
      status: getModalCurrentStep({
        isConnecting: !!isConnecting,
        isFailure: !!isFailure,
        isNotAllowed: !!isNotAllowed,
        isSuccess: !!isSuccess,
        isRefusedByUser: !!isRefusedByUser,
      }),
    }),
    [isOpenModal, isConnecting, isFailure, isNotAllowed, isSuccess, isRefusedByUser],
  );

  const isModalLoading = useMemo<boolean>(
    () => screenShareData.status === TemConnectionBodySteps.Connecting,
    [screenShareData],
  );
  const isModalSuccess = useMemo<boolean>(
    () => screenShareData.status === TemConnectionBodySteps.Success,
    [screenShareData],
  );
  const isModalError = useMemo<boolean>(
    () =>
      [
        TemConnectionBodySteps.Failure,
        TemConnectionBodySteps.NotAllowed,
        TemConnectionBodySteps.RefusedByCustomer,
      ].includes(screenShareData.status),
    [screenShareData],
  );

  useImperativeHandle(ref, () => ({ tryCheckDeviceConnection }), [tryCheckDeviceConnection]);

  const handleCloseModal = useCallback(() => onClose?.(), [onClose]);

  return {
    handleCloseModal,
    handleConfirmModal: tryCheckDeviceConnection,
    screenShareData,
    isModalError,
    isModalLoading,
    isModalSuccess,
  };
}
